import { FC, ReactNode, useEffect, useState } from "react";

import type { MealInsightHistoryResponse } from "@9amhealth/openapi";
import styled from "@emotion/styled";
import { APP_BREAKPOINT } from "src/constants/layout";
import { fileState } from "src/state/state";

import {
  IconMacroCarbs,
  IconMacroFat,
  IconMacroFiber,
  IconMacroProtein
} from "./MacroIcons";
import { formatCalorieRange, formatMealTime } from "./mealInsightHelpers";

const THUMB_SIZE = 108;

const Card = styled.button`
  position: relative;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  padding: 1rem;
  background: var(--color-white, #fff);
  border-radius: 0.5rem;
  box-shadow:
    0 5px 8px rgba(69, 49, 22, 0.08),
    0 20px 14.5px rgba(69, 49, 22, 0.04);

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    padding: 1.5rem;
  }
`;

const Thumb = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 3.375rem;
  height: 3.375rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #d9d9d9;
`;

const ThumbImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Body = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
  padding-top: 2px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const Name = styled.p`
  flex: 1 0 0;
  min-width: 0;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: -0.28px;
  color: color-mix(in srgb, var(--color-charcoal, #212121) 80%, transparent);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TimePill = styled.span`
  flex-shrink: 0;
  padding: 2px 5px 2px 6px;
  border-radius: 120px;
  background: var(--color-fog, #f4f4f4);
  font-size: 0.75rem;
  line-height: 1.33;
  color: color-mix(in srgb, var(--color-charcoal, #212121) 60%, transparent);
  white-space: nowrap;
`;

const Calories = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.375;
  letter-spacing: -0.32px;
  color: var(--color-charcoal, #212121);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Macros = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 4px;
  padding: 0.25rem 0;
`;

const MacroPill = styled.span<{ $color: string; $background: string }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 8px 2px 5px;
  border-radius: 200px;
  background: ${(props) => props.$background};
  color: ${(props) => props.$color};
  font-size: 0.875rem;
  line-height: 1.43;
  letter-spacing: -0.28px;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
    width: 1.1875rem;
    height: 1.1875rem;
  }
`;

interface MacroPillConfig {
  key: "carbs" | "fiber" | "fat" | "protein";
  value?: number;
  color: string;
  background: string;
  icon: ReactNode;
}

const formatGrams = (value: number): string => `${Math.round(value)}g`;

interface MealCardProps {
  meal: MealInsightHistoryResponse;
  onClick: (meal: MealInsightHistoryResponse) => void;
}

const MealCard: FC<MealCardProps> = ({ meal, onClick }) => {
  const [imageUrl, setImageUrl] = useState<string>("");

  useEffect(() => {
    let active = true;
    void fileState
      .loadPreview(meal.fileId, THUMB_SIZE, THUMB_SIZE)
      .then((url) => {
        if (active && url) {
          setImageUrl(url);
        }
      });
    return () => {
      active = false;
    };
  }, [meal.fileId]);

  const time = formatMealTime(meal.takenAt);
  const calories = formatCalorieRange(
    meal.nutrients?.caloriesRange?.from,
    meal.nutrients?.caloriesRange?.to
  );

  const macros: MacroPillConfig[] = [
    {
      key: "protein",
      value: meal.nutrients?.protein,
      color: "#905c81",
      background: "rgba(144, 92, 129, 0.08)",
      icon: <IconMacroProtein />
    },
    {
      key: "fiber",
      value: meal.nutrients?.fiber,
      color: "#0e843a",
      background: "rgba(14, 132, 58, 0.04)",
      icon: <IconMacroFiber />
    },
    {
      key: "fat",
      value: meal.nutrients?.totalFats,
      color: "#ac8b6a",
      background: "rgba(172, 139, 106, 0.08)",
      icon: <IconMacroFat />
    },
    {
      key: "carbs",
      value: meal.nutrients?.totalCarbohydrates,
      color: "#b36d3a",
      background: "rgba(179, 109, 58, 0.08)",
      icon: <IconMacroCarbs />
    }
  ];

  const visibleMacros = macros.filter(
    (macro) => macro.value != null && macro.value > 0
  );

  return (
    <Card
      type="button"
      onClick={() => onClick(meal)}
      aria-label={meal.mealName}
    >
      <Thumb>{imageUrl && <ThumbImage src={imageUrl} alt="" />}</Thumb>
      <Body>
        <TitleRow>
          <Name>{meal.mealName}</Name>
          <TimePill>{time}</TimePill>
        </TitleRow>
        {calories && <Calories>{calories}</Calories>}
        {visibleMacros.length > 0 && (
          <Macros>
            {visibleMacros.map((macro) => (
              <MacroPill
                key={macro.key}
                $color={macro.color}
                $background={macro.background}
              >
                {macro.icon}
                {formatGrams(macro.value as number)}
              </MacroPill>
            ))}
          </Macros>
        )}
      </Body>
    </Card>
  );
};

export default MealCard;
